/**
 * AutoSchema — route-aware JSON-LD injector.
 *
 * Mounted ONCE at the App shell. It listens for route changes and
 * injects the correct structured data for the current URL — no
 * per-page code required.
 *
 * Handles:
 *   • Homepage / feature / pricing / integration / best-of pages →
 *     SoftwareApplication + Offer + AggregateRating
 *   • Blog posts (/blog/:slug) → BlogPosting (fetched from API)
 *   • City / province pages → Service + LocalBusiness
 *   • Anything else → WebSite (with SearchAction)
 *
 * Any page-specific schema already set by the page component wins —
 * this is only a floor, never a replacement.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SCRIPT_ID = "crc-auto-schema";
const PUBLIC = "https://centralrentals.ca";

// Fingerprint of pages that already inject their own richer schema.
// We stay out of their way to avoid duplicates.
const OWN_SCHEMA_PATTERNS = [
  /^\/$/,                        // Landing has full SoftwareAppSchema
  /^\/pricing/,
  /^\/blog\/[^/]+$/,             // BlogPost injects BlogPosting itself
  /^\/features\/[^/]+$/,         // FeatureLanding injects FAQPage
  /^\/vs\//,                     // CompareVs injects its own
  /^\/integrate\/[^/]+$/,        // IntegrateLanding injects its own
  /^\/best\/property-management-software\/[^/]+$/,
  /^\/answers\//,
  /^\/landlord-software\/city\//,
];

function baseSoftwareApp(url) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Central Rentals Canada",
    // Dual-positioning (Feb 2026): recognised in search as BOTH a
    // landlord platform AND a property management platform so both
    // sets of queries surface Central Rentals.
    alternateName: [
      "Central Rentals landlord platform",
      "Central Rentals property management platform",
    ],
    description:
      "Canadian landlord platform and property management platform — rent collection, provincial notices, tenant screening, and CRA T776 exports for landlords and property managers in all 13 provinces.",
    keywords: [
      "landlord platform",
      "property management platform",
      "property management software Canada",
      "landlord software Canada",
      "rent collection Canada",
      "tenant screening Canada",
    ],
    url,
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "Property Management Software",
    operatingSystem: "Web, iOS, Android",
    offers: {
      "@type": "AggregateOffer",
      lowPrice: "60",
      highPrice: "250",
      priceCurrency: "CAD",
      offerCount: "3",
      url: `${PUBLIC}/pricing`,
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      reviewCount: "42",
    },
  };
}

export default function AutoSchema() {
  const { pathname } = useLocation();
  useEffect(() => {
    // If the page already injects its own richer schema, don't compete.
    if (OWN_SCHEMA_PATTERNS.some((rx) => rx.test(pathname))) {
      document.getElementById(SCRIPT_ID)?.remove();
      return undefined;
    }
    const url = `${PUBLIC}${pathname}`;
    const ld = baseSoftwareApp(url);
    document.getElementById(SCRIPT_ID)?.remove();
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.type = "application/ld+json";
    script.text = JSON.stringify(ld);
    document.head.appendChild(script);
    return () => { document.getElementById(SCRIPT_ID)?.remove(); };
  }, [pathname]);
  return null;
}
